import React, { useMemo, useState, useEffect } from "react";
import { useGetCentres } from "../../api/settings";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Icon } from "@iconify/react";

const DropdownIcon = (props) => (
  <Icon icon="eva:arrow-ios-downward-fill" width={18} height={18} {...props} />
);

function CenterFilter({ hubIds = [], spokeIds = [], onChange, component }) {
  // Pagination states
  const [page, setPage] = useState(1);
  const limit = 50;
  const [allCentres, setAllCentres] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const {
    centres = [],
    isLoading,
    pagination,
  } = useGetCentres({
    centerType: "hub",
    onlyActive: "active",
    page,
    limit,
  });

  const [search, setSearch] = useState("");

  // Append paginated centres
  useEffect(() => {
    if (!isLoading && Array.isArray(centres)) {
      setAllCentres((prev) => {
        if (page === 1) return [...centres];

        const existingIds = new Set(prev.map((item) => item.id));

        const newItems = centres.filter((item) => !existingIds.has(item.id));

        return [...prev, ...newItems];
      });

      setLoadingMore(false);
    }
  }, [centres, isLoading, page]);

  // Infinite scroll
  const handleScroll = (e) => {
    const target = e.currentTarget;

    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    const hasMore = page < (pagination?.totalPages || 1);

    if (isNearBottom && hasMore && !loadingMore) {
      setLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  /* ---------------- HUB OPTIONS ---------------- */
  const hubOptions = useMemo(
    () =>
      allCentres.map((h) => ({
        label: h.name?.trim(),
        value: h.id,
        spokes: h.spokeCenters || [],
      })),
    [allCentres],
  );

  /* ---------------- SPOKE OPTIONS ---------------- */
  const spokeOptions = useMemo(
    () =>
      hubOptions
        .filter((h) => hubIds.includes(h.value))
        .flatMap((h) => h.spokes)
        .map((s) => ({
          label: s.name?.trim(),
          value: s.id,
        })),
    [hubOptions, hubIds],
  );

  /* ---------------- SELECT COMPONENT ---------------- */
  const renderSelect = ({ label, options, value, onChange, disabled }) => {
    const filtered = useMemo(() => {
      return options.filter((o) =>
        String(o?.label || "")
          .trim()
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      );
    }, [options, search]);

    const handleItemClick = (selectedValue) => {
      const alreadySelected = value.includes(selectedValue);

      if (alreadySelected) {
        onChange(value.filter((v) => v !== selectedValue));
      } else {
        onChange([...value, selectedValue]);
      }
    };

    return (
      <FormControl size="small">
        <Select
          multiple
          displayEmpty
          disabled={disabled}
          value={value}
          IconComponent={DropdownIcon}
          onOpen={() => setSearch("")}
          onChange={() => {}}
          renderValue={() => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: 14,
                fontWeight: 500,
                color: "var(--app-text)",
              }}
            >
              <span>{label}</span>

              {value.length > 0 && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    borderRadius: "50%",
                    backgroundColor: "#7C7CFF",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {value.length}
                </Box>
              )}
            </Box>
          )}
          sx={{
            height: 38,
            width:
              label === "Spoke Centre" && value.length > 0
                ? 158
                : value.length > 0
                  ? 145
                  : 135,
            borderRadius: "10px",
            margin: "0 2px",
            backgroundColor:
              component === "interpretations" ||
              component === "referred-cases" ||
              component === "centres" ||
              component === "members"
                ? "var(--app-surface)"
                : "var(--app-surface)",

            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },

            "& .MuiSelect-select": {
              fontSize: 14,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            },
          }}
          MenuProps={{
            disableAutoFocusItem: true,
            autoFocus: false,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            PaperProps: {
              onScroll: handleScroll,
              sx: {
                mt: 1,
                width: 260,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: "12px",
                border: "1px solid var(--app-border)",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",

                "&::-webkit-scrollbar": {
                  display: "none",
                },

                scrollbarWidth: "none",
                msOverflowStyle: "none",
              },
            },
          }}
        >
          {/* SEARCH FIELD */}
          <Box
            component="li"
            sx={{
              p: 1,
              position: "sticky",
              top: 0,
              background: "var(--app-surface)",
              zIndex: 10,
              listStyle: "none",
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "12px",

                  "& fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&:hover fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "var(--app-border)",
                    borderWidth: "1px",
                  },

                  "&.Mui-focused": {
                    boxShadow: "none",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* LOADING */}
          {isLoading && page === 1 ? (
            <MenuItem disabled>
              <CircularProgress size={18} />
            </MenuItem>
          ) : filtered.length > 0 ? (
            filtered.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disableRipple
                onClick={(e) => {
                  e.stopPropagation();
                  handleItemClick(opt.value);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  py: 1,
                  cursor: "pointer",

                  "&:hover": {
                    backgroundColor: "var(--app-surface-2)",
                  },
                }}
              >
                <Checkbox
                  checked={value.includes(opt.value)}
                  size="small"
                  sx={{
                    color: "var(--app-border)",

                    "&.Mui-checked": {
                      color: "#5751E9",
                    },

                    "& .MuiSvgIcon-root": {
                      borderRadius: "8px",
                      fontSize: 20,
                    },
                  }}
                />

                <ListItemText
                  primary={opt.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText
                primary="No results found"
                primaryTypographyProps={{
                  fontSize: 14,
                  color: "var(--app-text-muted)",
                }}
              />
            </MenuItem>
          )}

          {loadingMore && (
            <MenuItem disabled>
              <CircularProgress size={18} />
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  };

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {/* HUB CENTRE */}
      {renderSelect({
        label: "Hub Centre",
        options: hubOptions,
        value: hubIds,
        onChange: (ids) => {
          onChange({
            hubIds: ids,
            spokeIds: [],
          });
        },
        disabled: false,
      })}

      {/* SPOKE CENTRE */}
      {renderSelect({
        label: "Spoke Centre",
        options: spokeOptions,
        value: spokeIds,
        onChange: (ids) => {
          onChange({
            hubIds,
            spokeIds: ids,
          });
        },
        disabled: hubIds.length === 0 || spokeOptions.length === 0,
      })}
    </Box>
  );
}

export default CenterFilter;
